import React, { FC, useState } from 'react';
import { styled } from 'linaria/react';
import { Link } from '@mtsbank/ui-kit';
import { SwitchReplenishmentMethod } from '@features/replenishment/method-switch';
import { CountryList, OldCountrySelect } from '@features/country/select-country';
import { VoucherList } from '@features/voucher/select-voucher';
import { FetchHintsResponseType } from '@entities/hint';
import { withTheme } from '@shared/lib';
import { Partners, usePartner } from '@shared/lib/partner';
import { useLockScroll } from '@shared/lib/react';
import { AlertBanner } from '@shared/ui/molecules';
import { Spacer } from '@shared/ui/atoms';
import { Payee } from '@root/api/ump-catalog-manager';
import NewAccountInstructionsModal from './NewAccountInstructionsModal';
import RegionsWithLimitsInstructionsModal from './RegionsWithLimitsInstructionsModal';
interface ReplenishmentInfoProps {
  isSteam: boolean;
  countryVouchers: {
    country: string;
    icon: string;
  }[];
  denominationsByCountry: Record<string, Payee[]>;
  activeService: Payee;
  showVouchersForm: boolean;
  setShowVouchersForm: (showVouchersForm: boolean) => void;
  hints: FetchHintsResponseType;
}
const ReplenishmentInfo: FC<ReplenishmentInfoProps> = ({
  isSteam,
  countryVouchers,
  denominationsByCountry,
  activeService,
  showVouchersForm,
  setShowVouchersForm,
  hints
}) => {
  const [isNewAccountModalActive, setIsNewAccountModalActive] = useState(false);
  const [isRegionsWithLimitsModalActive, setIsRegionsWithLimitsModalActive] = useState(false);
  useLockScroll(isNewAccountModalActive || isRegionsWithLimitsModalActive);
  const {
    partner
  } = usePartner();
  const handleIsNewAccountModalSwitch = () => {
    setIsNewAccountModalActive(prev => !prev);
  };
  const handleIsRegionsWithLimitsModalSwitch = () => {
    setIsRegionsWithLimitsModalActive(prev => !prev);
  };
  const getCountryBlockTitle = () => {
    if (!isSteam && countryVouchers?.length > 1) {
      return 'Выберите регион аккаунта';
    }
    return 'Регион аккаунта';
  };
  const isRegions = countryVouchers?.[0]?.country !== 'noRegion';
  return <>
      <WrapperContent>
        {isSteam && partner !== Partners.SMARTSHELL && <>
            <Text>Выберите способ пополнения</Text>
            <Spacer space={16} spacetablet={16} spacemob={16} />
            <SwitchReplenishmentMethod showVouchersForm={showVouchersForm} setShowVouchersForm={setShowVouchersForm} />
            <Spacer space={32} spacetablet={32} spacemob={24} />
          </>}
        {isRegions && <>
            <Text>{getCountryBlockTitle()}</Text>
            <Spacer space={16} />
            {isSteam && !showVouchersForm || countryVouchers?.length === 1 ? <CountryList isSteam={isSteam} showVouchersForm={showVouchersForm} activeService={activeService} countryVouchers={countryVouchers} /> : <OldCountrySelect isSteam={isSteam} showVouchersForm={showVouchersForm} countryVouchers={countryVouchers} />}
            <Spacer space={16} spacetablet={16} spacemob={16} />
            <Info>
              <InfoTitle>
                {activeService?.features?.merchant_association_for_WEB && <AlertBanner>
                    Ознакомьтесь с инструкцией перед пополнением Steam:{' '}
                    <NewCardLink target="_blank" onClick={handleIsNewAccountModalSwitch}>
                      для новых аккаунтов
                    </NewCardLink>{' '}
                    и&nbsp;
                    <NewCardLink target="_blank" onClick={handleIsRegionsWithLimitsModalSwitch}>
                      регионов с ограничениями (Крым, ЛНР, ДНР)
                    </NewCardLink>
                  </AlertBanner>}
              </InfoTitle>
            </Info>
          </>}
        {showVouchersForm && <>
            {isRegions && <Spacer space={32} spacetablet={32} spacemob={24} />}
            <VouchersWrapper>
              <Text>Выберите номинал</Text>
              <Spacer space={16} />
              <VoucherList denominationsByCountry={denominationsByCountry} activeService={activeService} />
            </VouchersWrapper>
          </>}
      </WrapperContent>

      <NewAccountInstructionsModal isOpen={isNewAccountModalActive} handleClose={handleIsNewAccountModalSwitch} content={hints.steam.newAccounts} />
      <RegionsWithLimitsInstructionsModal isOpen={isRegionsWithLimitsModalActive} handleClose={handleIsRegionsWithLimitsModalSwitch} content={hints.steam.restrictedAccounts} />
    </>;
};
export default ReplenishmentInfo;
const _exp = /*#__PURE__*/() => ({
  theme
}) => theme.colors.bg.secondary;
const WrapperContent = withTheme(/*#__PURE__*/styled('div')({
  name: "WrapperContent",
  class: "wgs0scl",
  propsAsIs: false,
  vars: {
    "wgs0scl-0": [_exp()]
  }
}));
const _exp2 = /*#__PURE__*/() => ({
  theme
}) => theme.font.family.MTSWide;
const _exp3 = /*#__PURE__*/() => ({
  theme
}) => theme.font.size.sm;
const _exp4 = /*#__PURE__*/() => ({
  theme
}) => theme.font.weight.medium;
const _exp5 = /*#__PURE__*/() => ({
  theme
}) => theme.font.lineHeight.xs;
const _exp6 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.primary;
const _exp7 = /*#__PURE__*/() => ({
  theme
}) => theme.font.size.md;
const Text = withTheme(/*#__PURE__*/styled('div')({
  name: "Text",
  class: "t130k1zt",
  propsAsIs: false,
  vars: {
    "t130k1zt-0": [_exp2()],
    "t130k1zt-1": [_exp3(), "px"],
    "t130k1zt-2": [_exp4()],
    "t130k1zt-3": [_exp5(), "px"],
    "t130k1zt-4": [_exp6()],
    "t130k1zt-5": [_exp7(), "px"]
  }
}));
const VouchersWrapper = /*#__PURE__*/styled('div')({
  name: "VouchersWrapper",
  class: "vrxhnkg",
  propsAsIs: false
});
const Info = /*#__PURE__*/styled('div')({
  name: "Info",
  class: "ijg44sf",
  propsAsIs: false
});
const InfoTitle = /*#__PURE__*/styled('div')({
  name: "InfoTitle",
  class: "isqhll4",
  propsAsIs: false
});
const _exp8 = /*#__PURE__*/() => Link;
const _exp9 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.primaryLink;
const _exp0 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.state.primaryLinkHover;
const NewCardLink = withTheme(/*#__PURE__*/styled(_exp8())({
  name: "NewCardLink",
  class: "nstfckj",
  propsAsIs: true,
  vars: {
    "nstfckj-0": [_exp9()],
    "nstfckj-1": [_exp0()]
  }
}));

require("./ReplenishmentInfo.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./ReplenishmentInfo.tsx");